import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Box,
  Autocomplete,
} from '@mui/material';
import { apiService } from '../services/api';
import { displayName } from '../types';

interface Entity {
  id: string;
  name: string;
}

interface Department {
  id: string;
  name: string;
}

interface Employee {
  id: string;
  fullName: string;
  nickname?: string;
}

interface CreateRequisitionModalProps {
  open: boolean;
  onClose: () => void;
  onRequisitionCreated: () => void;
}

const CreateRequisitionModal: React.FC<CreateRequisitionModalProps> = ({
  open,
  onClose,
  onRequisitionCreated,
}) => {
  const [formData, setFormData] = useState({
    entityId: '',
    departmentId: '',
    title: '',
    level: '',
    location: '',
    employmentType: 'full_time',
    hiringManagerId: '',
    targetStartDate: '',
    maxSalary: '',
    minSalary: '',
    currency: 'USD',
    urgency: 'normal',
    description: '',
    requirements: '',
  });

  const [entities, setEntities] = useState<Entity[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [managers, setManagers] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    if (open) {
      const init = async () => {
        setDataLoading(true);
        try {
          const [entitiesData, empData] = await Promise.all([
            apiService.getEntities(),
            apiService.getEmployees(),
          ]);
          setEntities(entitiesData);
          setManagers(empData.filter((e: any) => e.employmentStatus === 'active'));
        } catch (err) {
          setError('Failed to load form data');
        } finally {
          setDataLoading(false);
        }
      };
      init();
    }
  }, [open]);

  useEffect(() => {
    if (formData.entityId) {
      loadDepartments();
    }
  }, [formData.entityId]);

  const loadEntities = async () => {
    try {
      setDataLoading(true);
      const entitiesData = await apiService.getEntities();
      setEntities(entitiesData);
    } catch (err) {
      console.error('Error loading entities:', err);
      setError('Failed to load entities');
    } finally {
      setDataLoading(false);
    }
  };

  const loadDepartments = async () => {
    if (!formData.entityId) return;
    try {
      const deptData = await apiService.getDepartments(formData.entityId);
      setDepartments(deptData);
    } catch (err) {
      console.error('Error loading departments:', err);
    }
  };

  const loadManagers = async () => {
    try {
      const empData = await apiService.getEmployees();
      setManagers(empData.filter((e: any) => e.employmentStatus === 'active'));
    } catch (err) {
      console.error('Error loading managers:', err);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      const requisitionData = {
        entityId: formData.entityId,
        departmentId: formData.departmentId,
        title: formData.title,
        level: formData.level || undefined,
        location: formData.location || undefined,
        employmentType: formData.employmentType,
        hiringManagerId: formData.hiringManagerId,
        targetStartDate: formData.targetStartDate || undefined,
        budgetMin: formData.minSalary ? parseFloat(formData.minSalary) : undefined,
        budgetMax: formData.maxSalary ? parseFloat(formData.maxSalary) : undefined,
        currency: formData.currency || undefined,
        urgency: formData.urgency || undefined,
        jobDescription: formData.description,
        requirements: formData.requirements || undefined,
      };

      await apiService.createRequisition(requisitionData);

      onRequisitionCreated();
      onClose();
      setFormData({
        entityId: '',
        departmentId: '',
        title: '',
        level: '',
        location: '',
        employmentType: 'full_time',
        hiringManagerId: '',
        targetStartDate: '',
        maxSalary: '',
        minSalary: '',
        currency: 'USD',
        urgency: 'normal',
        description: '',
        requirements: '',
      });
    } catch (err: any) {
      console.error('Error creating requisition:', err);
      setError(err.message || 'Failed to create requisition');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = formData.entityId && formData.departmentId && formData.title && 
                     formData.hiringManagerId && formData.description;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create Job Requisition</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {dataLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={6}>
              <Autocomplete
                options={entities}
                getOptionLabel={(option) => option.name}
                value={entities.find(e => e.id === formData.entityId) || null}
                onChange={(event, newValue) => {
                  handleInputChange('entityId', newValue?.id || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Entity"
                    required
                    helperText="Select company entity"
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>
            <Grid size={6}>
              <Autocomplete
                options={departments}
                getOptionLabel={(option) => option.name}
                value={departments.find(d => d.id === formData.departmentId) || null}
                onChange={(event, newValue) => {
                  handleInputChange('departmentId', newValue?.id || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Department"
                    required
                    helperText="Select department"
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={!formData.entityId}
              />
            </Grid>
            <Grid size={8}>
              <TextField
                fullWidth
                label="Job Title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="e.g., Senior Software Engineer"
                required
              />
            </Grid>
            <Grid size={4}>
              <TextField
                fullWidth
                label="Level"
                value={formData.level}
                onChange={(e) => handleInputChange('level', e.target.value)}
                placeholder="e.g., Senior, Mid, Junior"
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                placeholder="e.g., New York, Remote"
              />
            </Grid>
            <Grid size={6}>
              <Autocomplete
                options={[
                  { value: 'full_time', label: 'Full Time' },
                  { value: 'part_time', label: 'Part Time' },
                  { value: 'contract', label: 'Contract' },
                  { value: 'intern', label: 'Intern' },
                ]}
                getOptionLabel={(option) => option.label}
                value={{ value: formData.employmentType, label: formData.employmentType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) }}
                onChange={(event, newValue) => {
                  handleInputChange('employmentType', newValue?.value || 'full_time');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Employment Type"
                    helperText="Select employment type"
                  />
                )}
                isOptionEqualToValue={(option, value) => option.value === value.value}
              />
            </Grid>
            <Grid size={12}>
              <Autocomplete
                options={managers}
                getOptionLabel={(option) => option ? displayName(option as any) : ''}
                value={managers.find(m => m.id === formData.hiringManagerId) || null}
                onChange={(event, newValue) => {
                  handleInputChange('hiringManagerId', newValue?.id || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Hiring Manager"
                    required
                    helperText="Select hiring manager"
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>
            <Grid size={4}>
              <TextField
                fullWidth
                label="Min Salary"
                type="number"
                value={formData.minSalary}
                onChange={(e) => handleInputChange('minSalary', e.target.value)}
                placeholder="50000"
              />
            </Grid>
            <Grid size={4}>
              <TextField
                fullWidth
                label="Max Salary"
                type="number"
                value={formData.maxSalary}
                onChange={(e) => handleInputChange('maxSalary', e.target.value)}
                placeholder="80000"
                inputProps={{ min: formData.minSalary }}
              />
            </Grid>
            <Grid size={4}>
              <TextField
                fullWidth
                label="Target Start Date"
                type="date"
                value={formData.targetStartDate}
                onChange={(e) => handleInputChange('targetStartDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Job Description"
                multiline
                rows={4}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Describe the role responsibilities and what the candidate will do..."
                required
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Requirements"
                multiline
                rows={3}
                value={formData.requirements}
                onChange={(e) => handleInputChange('requirements', e.target.value)}
                placeholder="List required skills, experience, qualifications..."
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !isFormValid || dataLoading}
        >
          {loading ? <CircularProgress size={20} /> : 'Create Requisition'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateRequisitionModal;